import { Node } from '@tiptap/core';
import { blockNodeSpec } from './block';

// Chapters: the chapter markers of the episode this post is linked to. The node holds nothing but the
// block placeholder; Newsletters::RenderBlocks reads the episode's own timestamps at display time. The
// editor chrome (dashboard/shared/tiptap/_chapters_editor.html.erb) shows a server preview of that list
// plus a note that chapters are edited on the episode.
//
// The slash item is only offered when the post being edited has an episode with chapters. The page passes
// that as data-tiptap-block-capabilities-value ({ chapters: { available, episode_id, edit_url } }), which
// tiptap_controller hands to this extension as options.capabilities.

export const Chapters = Node.create({
  name: 'chapters',

  ...blockNodeSpec('chapters'),

  addOptions() {
    return {
      templates: null,         // { editor_html } — injected by the tiptap controller from the server template
      capabilities: null,      // { available, episode_id, edit_url } — injected by the tiptap controller
      fetchRenderedHtml: null, // async (attrs) => string | null — injected by the tiptap controller
      onRenderedHtmlFetched: null,
    };
  },

  addNodeView() {
    return ({ editor, getPos }) => {
      const extension = this;
      const dom = document.createElement('div');
      dom.setAttribute('data-chapters-node', '');
      dom.innerHTML = extension.options.templates?.editor_html
        || '<div class="my-3 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-500">Chapters</div>';

      const preview = dom.querySelector('[data-preview]');
      const hint = dom.querySelector('[data-preview-hint]');
      const editLink = dom.querySelector('[data-edit-link]');
      const { episode_id: episodeId, edit_url: editUrl } = extension.options.capabilities || {};

      if (editLink) {
        if (editUrl) editLink.setAttribute('href', editUrl);
        else editLink.classList.add('hidden');
      }

      if (extension.options.fetchRenderedHtml && episodeId) {
        extension.options.fetchRenderedHtml({ episodeId }).then(html => {
          if (!preview) return;
          preview.innerHTML = html || '';
          hint?.classList.toggle('hidden', Boolean(html));
        });
      }

      // The preview is made of real links; never navigate away from the editor.
      preview?.addEventListener('click', e => e.preventDefault());

      dom.querySelector('[data-remove]')?.addEventListener('mousedown', e => {
        e.preventDefault();
        const pos = typeof getPos === 'function' ? getPos() : null;
        if (pos === null || pos === undefined) return;
        const current = editor.state.doc.nodeAt(pos);
        if (current) editor.chain().focus().deleteRange({ from: pos, to: pos + current.nodeSize }).run();
      });

      return {
        dom,
        stopEvent: event => Boolean(event.target?.closest?.('a, button')),
        ignoreMutation: () => true,
      };
    };
  },

  addCommands() {
    return {
      insertChapters: () => ({ chain }) =>
        chain().insertContent({ type: this.name }).run(),
    };
  },
});

function chaptersAvailable(editor) {
  const extension = editor.extensionManager.extensions.find(ext => ext.name === 'chapters');
  return Boolean(extension?.options.capabilities?.available);
}

export const chaptersSlashItem = {
  title: 'Chapter markers',
  description: "The episode's chapters, linked to the moment they start",
  icon: '☰',
  group: 'Episode',
  keywords: ['chapters', 'timestamps', 'markers', 'episode', 'contents'],
  canInsert: (editor) => {
    if (!chaptersAvailable(editor)) return false;
    let hasChapters = false;
    editor.state.doc.descendants(node => { if (node.type.name === 'chapters') hasChapters = true; });
    return !hasChapters;
  },
  command: ({ editor, range }) => {
    editor.chain().focus().deleteRange(range).insertChapters().run();
  },
};
